import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Tag,
  message,
} from 'antd';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import tradeCommitmentService, {
  DIRECTION_LABELS,
  STATUS_LABELS,
  DELIVERY_STATUS_LABELS,
} from '../../../../services/tradeCommitmentService';
import purchaseDealSheetService from '../../../../services/purchaseDealSheetService';
import investorOnboardingService from '../../../../services/investorOnboardingService';
import CounterpartyCreatableSelect from './CounterpartyCreatableSelect';
import { apiErrMsg, formatMoney } from './tradeCommitment.helpers';
import { PARTNER_SEARCH_MIN, SUPPLIER_SEARCH_MIN } from './tradeCommitment.constants';

export default function TradeCommitmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE);

  const [record, setRecord] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [linkingId, setLinkingId] = useState('');
  const [convertingPds, setConvertingPds] = useState(false);
  const [editCounterparty, setEditCounterparty] = useState(null);
  const [editForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await tradeCommitmentService.get(id);
      setRecord(data);

      if (data.status === 'ACTIVE' && !data.linked_order_id && !data.linked_purchase_deal_sheet_id) {
        const linkData = await tradeCommitmentService.getSuggestedLinks(id);
        setSuggestions(linkData.suggestions || []);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      setError(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchCounterparties = useCallback(
    async (search) => {
      if (record?.direction === 'BUY') {
        return purchaseDealSheetService.listSuppliers({ search, limit: 50 });
      }
      const users = await investorOnboardingService.listIFAs({
        search,
        page: 1,
        limit: 50,
      });
      return { items: users?.data || users || [] };
    },
    [record?.direction],
  );

  const openEdit = () => {
    editForm.setFieldsValue({
      quantity: record.quantity,
      price_per_share: record.price_per_share,
      notes: record.notes,
      counterparty_id: record.counterparty_name,
    });
    setEditCounterparty(
      record.counterparty_name
        ? { name: record.counterparty_name, uuid: record.counterparty_id }
        : null,
    );
    setEditOpen(true);
  };

  const handleEdit = async (values) => {
    const counterpartyName =
      editCounterparty?.name?.trim() || values.counterparty_id?.trim?.() || values.counterparty_id;
    if (!counterpartyName) {
      message.error('Enter or select a counterparty');
      return;
    }

    setEditSaving(true);
    try {
      const payload = {
        quantity: values.quantity,
        price_per_share: values.price_per_share,
        counterparty_name: counterpartyName,
        notes: values.notes,
        counterparty_type: record.direction === 'SELL' ? 'PARTNER' : 'SUPPLIER',
      };
      if (editCounterparty?.uuid && !editCounterparty?.custom) {
        payload.counterparty_id = editCounterparty.uuid;
      } else {
        payload.counterparty_id = null;
      }

      await tradeCommitmentService.update(id, payload);
      message.success('Commitment updated');
      setEditOpen(false);
      load();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancel = async () => {
    try {
      await tradeCommitmentService.cancel(id);
      message.success('Commitment cancelled');
      load();
    } catch (err) {
      message.error(apiErrMsg(err));
    }
  };

  const handleLink = async (suggestion) => {
    setLinkingId(suggestion.target_id);
    try {
      if (suggestion.target_type === 'purchase_deal_sheet') {
        await tradeCommitmentService.linkToPurchaseDealSheet(id, suggestion.target_id);
      } else {
        await tradeCommitmentService.linkToOrder(id, suggestion.target_id);
      }
      message.success(`Linked to ${suggestion.ref}`);
      load();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setLinkingId('');
    }
  };

  const handleQuickCreatePds = async () => {
    setConvertingPds(true);
    try {
      const result = await tradeCommitmentService.convertToPurchaseDealSheet(id, {});
      message.success('Purchase deal sheet created and linked');
      const pdsId = result.purchase_deal_sheet?.uuid ?? result.purchase_deal_sheet?.id;
      if (pdsId) {
        navigate(`/pre-ipo/purchase-deal-sheet/${pdsId}`);
        return;
      }
      load();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setConvertingPds(false);
    }
  };

  const handleCreatePdsReview = () => {
    navigate(`/pre-ipo/purchase-deal-sheet/create?from_commitment=${encodeURIComponent(id)}`);
  };

  if (loading) {
    return (
      <DashboardPageWrapper title="Trade Commitment">
        <p>Loading…</p>
      </DashboardPageWrapper>
    );
  }

  if (!record) {
    return (
      <DashboardPageWrapper title="Trade Commitment">
        <Alert type="error" message={error || 'Not found'} />
      </DashboardPageWrapper>
    );
  }

  const isActive = record.status === 'ACTIVE';
  const isBuyLeg = record.direction === 'BUY';
  const canCreatePds =
    canManage && isActive && isBuyLeg && !record.linked_purchase_deal_sheet_id;
  const settlementMenu = isBuyLeg
    ? '/pre-ipo/purchase-deal-sheet/create'
    : '/pre-ipo/order-management';

  return (
    <DashboardPageWrapper title={`Trade Commitment ${record.ref}`}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button onClick={() => navigate('/pre-ipo/trade-commitments')}>Back</Button>
        {canManage && isActive && (
          <>
            <Button onClick={openEdit}>Edit</Button>
            <Button danger onClick={handleCancel}>
              Cancel commitment
            </Button>
          </>
        )}
        {canCreatePds && record.counterparty_id && (
          <Button type="primary" loading={convertingPds} onClick={handleQuickCreatePds}>
            Create purchase deal sheet
          </Button>
        )}
        {canCreatePds && (
          <Button onClick={handleCreatePdsReview}>Create PDS (review)</Button>
        )}
        {record.linked_order_id && (
          <Button onClick={() => navigate(`/pre-ipo/order-management/${record.linked_order_id}`)}>
            Open order
          </Button>
        )}
        {record.linked_purchase_deal_sheet_id && (
          <Button
            onClick={() => navigate(`/pre-ipo/purchase-deal-sheet/${record.linked_purchase_deal_sheet_id}`)}
          >
            Open purchase deal sheet
          </Button>
        )}
      </div>

      {isActive && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Provisional deal — edit here; settle elsewhere"
          description={
            <>
              Update quantity, price, or counterparty with Edit. Payments and UTR belong on the{' '}
              {isBuyLeg ? 'Purchase Deal Sheet' : 'Order'} — use{' '}
              {canCreatePds && record.counterparty_id ? (
                <>
                  <Button type="link" className="p-0 h-auto" onClick={handleQuickCreatePds}>
                    Create purchase deal sheet
                  </Button>
                  {' or '}
                </>
              ) : null}
              {canCreatePds ? (
                <Button type="link" className="p-0 h-auto" onClick={handleCreatePdsReview}>
                  Create PDS (review)
                </Button>
              ) : (
                <Button type="link" className="p-0 h-auto" onClick={() => navigate(settlementMenu)}>
                  {isBuyLeg ? 'Purchase Deal Sheet' : 'Order management'}
                </Button>
              )}
              {canCreatePds ? ', or create separately and ' : ', then '}
              link it here so the ledger tracks outstanding correctly.
            </>
          }
        />
      )}

      {isActive && suggestions.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Existing settlement record found"
          description={
            <ul className="mt-2 space-y-2 pl-0 list-none">
              {suggestions.map((s) => (
                <li key={s.target_id} className="flex flex-wrap items-center gap-2">
                  <span>
                    Link to {s.ref} — {formatMoney(s.outstanding_amount)} outstanding
                    {' · '}
                    <Tag color={s.confidence === 'high' ? 'green' : 'orange'}>
                      {s.confidence === 'high' ? 'High confidence' : 'Possible match'}
                    </Tag>
                  </span>
                  {canManage && (
                    <Button
                      size="small"
                      type="primary"
                      loading={linkingId === s.target_id}
                      onClick={() => handleLink(s)}
                    >
                      Link
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {error && <Alert type="error" message={error} className="mb-4" showIcon />}

      <Card>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Ref">{record.ref}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag>{STATUS_LABELS[record.status]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Direction">{DIRECTION_LABELS[record.direction]}</Descriptions.Item>
          <Descriptions.Item label="Product">
            {record.product_short_name || record.product_name}
          </Descriptions.Item>
          <Descriptions.Item label="Quantity">{record.quantity}</Descriptions.Item>
          <Descriptions.Item label="Price">{record.price_per_share}</Descriptions.Item>
          <Descriptions.Item label="Expected">{formatMoney(record.expected_amount)}</Descriptions.Item>
          <Descriptions.Item label="Counterparty">{record.counterparty_name}</Descriptions.Item>
          <Descriptions.Item label="Delivery">
            {DELIVERY_STATUS_LABELS[record.delivery_status]} ({record.fulfilled_quantity} / {record.quantity})
          </Descriptions.Item>
          <Descriptions.Item label="Source">{record.source}</Descriptions.Item>
          {record.notes && (
            <Descriptions.Item label="Notes" span={2}>
              {record.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {record.linked_legs?.length > 0 && (
        <Card title="Linked leg (same spread)" className="mt-4">
          {record.linked_legs.map((leg) => (
            <div key={leg.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <strong>{DIRECTION_LABELS[leg.direction]}</strong>
                {' — '}
                {leg.counterparty_name}
                {' · '}
                {leg.quantity} @ {leg.price_per_share}
              </div>
              <Button type="link" onClick={() => navigate(`/pre-ipo/trade-commitments/${leg.id}`)}>
                {leg.ref}
              </Button>
            </div>
          ))}
        </Card>
      )}

      <Modal
        title={`Edit ${record.ref}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={1} className="w-full" />
          </Form.Item>
          <Form.Item name="price_per_share" label="Price per share" rules={[{ required: true }]}>
            <InputNumber min={0.01} step={0.01} className="w-full" />
          </Form.Item>
          <Form.Item
            name="counterparty_id"
            label={record.direction === 'BUY' ? 'Supplier' : 'Partner / counterparty'}
            rules={[{ required: true, message: 'Select or enter counterparty' }]}
          >
            <CounterpartyCreatableSelect
              minSearchLength={record.direction === 'BUY' ? SUPPLIER_SEARCH_MIN : PARTNER_SEARCH_MIN}
              placeholder="Search or type name"
              fetchOptions={fetchCounterparties}
              onSelectItem={setEditCounterparty}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={editSaving}>
            Save changes
          </Button>
        </Form>
      </Modal>
    </DashboardPageWrapper>
  );
}
